import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AppLayout } from "../components/tabon/AppLayout";
import { api, formatError } from "../lib/api";
import { ChevronLeft, Search, Save } from "lucide-react";

// Normalise a code exactly as the backend does: alphanumeric, uppercased, max 10.
const clean = (v) => (v || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);

// Menu → Item Codes: a searchable table of every item with an editable code column and one Save.
// This is how a venue enters hundreds of codes without opening each item.
export default function ItemCodes() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [codes, setCodes] = useState({});   // item_id -> edited code string
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [i, c] = await Promise.all([api.get("/menu/items"), api.get("/menu/categories")]);
      const its = i.data || [];
      setItems(its);
      setCats(c.data || []);
      setCodes(Object.fromEntries(its.map((it) => [it.id, it.item_code || ""])));
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const catName = (id) => (cats.find((c) => c.id === id) || {}).name || "—";

  // Duplicate codes across the current edits (non-empty) → highlight + block save.
  const dupCodes = useMemo(() => {
    const seen = {}, dup = new Set();
    Object.values(codes).forEach((raw) => {
      const code = clean(raw);
      if (!code) return;
      if (seen[code]) dup.add(code); else seen[code] = true;
    });
    return dup;
  }, [codes]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) =>
      (it.name || "").toLowerCase().includes(q) ||
      (it.item_code || "").toLowerCase().includes(q) ||
      catName(it.category_id).toLowerCase().includes(q));
  }, [items, search, cats]);

  const dirty = useMemo(
    () => items.some((it) => clean(codes[it.id]) !== (it.item_code || "")),
    [items, codes]);

  const save = async () => {
    if (dupCodes.size) { toast.error("Fix duplicate codes before saving"); return; }
    const changed = items
      .filter((it) => clean(codes[it.id]) !== (it.item_code || ""))
      .map((it) => ({ item_id: it.id, item_code: clean(codes[it.id]) }));
    if (!changed.length) { toast.info("No changes to save"); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/menu/item-codes", { codes: changed });
      toast.success(`Saved ${data.updated} item${data.updated === 1 ? "" : "s"}`);
      await load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSaving(false); }
  };

  return (
    <AppLayout>
      <div className="max-w-[820px] mx-auto px-4 md:px-6 py-6">
        <button onClick={() => navigate("/menu")} className="flex items-center gap-1 text-sm text-[#A0A0A0] hover:text-white mb-4">
          <ChevronLeft size={16} /> Back to menu
        </button>

        <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
          <div>
            <h1 className="text-xl font-black text-white">Item codes</h1>
            <p className="text-xs text-[#A0A0A0] mt-1">A short call-out code (e.g. 403) staff can search by and that prints on the KOT. Unique per venue.</p>
          </div>
          <button onClick={save} disabled={saving || !dirty || dupCodes.size > 0}
            className="flex items-center gap-1.5 bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-40 text-white text-sm font-bold rounded-lg px-4 py-2.5">
            <Save size={15} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>

        <div className="relative mb-3">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0A0A0]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search items, codes, or a category…"
            className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-[#6B6B6B] focus:border-[#9D00FF] focus:outline-none" />
        </div>

        {loading ? (
          <div className="text-sm text-[#A0A0A0] py-10">Loading…</div>
        ) : (
          <div className="bg-[#1A1A1A] border border-white/10 rounded-xl overflow-hidden">
            <div className="grid grid-cols-[1fr_120px] gap-2 px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-[#A0A0A0] border-b border-white/10">
              <span>Item</span><span>Code</span>
            </div>
            <div className="max-h-[62vh] overflow-y-auto">
              {visible.map((it) => {
                const code = clean(codes[it.id]);
                const isDup = code && dupCodes.has(code);
                return (
                  <div key={it.id} className="grid grid-cols-[1fr_120px] gap-2 px-4 py-2 items-center border-b border-white/5">
                    <div className="min-w-0">
                      <div className="text-sm text-white truncate">{it.name}</div>
                      <div className="text-[11px] text-[#A0A0A0] truncate">{catName(it.category_id)}</div>
                    </div>
                    <input
                      value={codes[it.id] ?? ""}
                      maxLength={10}
                      onChange={(e) => setCodes((p) => ({ ...p, [it.id]: clean(e.target.value) }))}
                      placeholder="—"
                      className={`w-full bg-[#0A0A0A] border rounded-lg px-3 py-2 text-sm text-white font-mono focus:outline-none ${isDup ? "border-red-500 focus:border-red-500" : "border-white/10 focus:border-[#9D00FF]"}`} />
                  </div>
                );
              })}
              {visible.length === 0 && <div className="text-sm text-[#555] px-4 py-8">No items match.</div>}
            </div>
          </div>
        )}
        {dupCodes.size > 0 && (
          <p className="text-[11px] text-red-400 mt-2">Duplicate codes: {[...dupCodes].join(", ")}. Each code must be unique.</p>
        )}
      </div>
    </AppLayout>
  );
}
